/**
 * Executive Force AI Application Registry
 * 
 * Central configuration for all applications in the Executive Force AI ecosystem.
 * This file defines the correct ports, URLs, and metadata for each application.
 * 
 * PRODUCTION-GRADE CONFIGURATION
 * - Correct frontend ports for user-facing navigation
 * - Backend ports documented for reference
 * - Environment-aware URL generation
 * - Type-safe application definitions
 */

export interface BusinessApplication {
  id: string;
  name: string;
  description: string;
  frontendUrl: string;
  backendUrl: string;
  icon: string; // Icon name from Material-UI Icons
  status: 'active' | 'available' | 'coming_soon';
  permissions: string[];
  category: 'core' | 'analytics' | 'operations' | 'monitoring';
}

/**
 * Environment configuration - Production Cloud Run URLs
 */
const PRODUCTION_URLS = {
  forceai: 'https://ef-strategy.co.uk',
  crm: 'https://ef-crm.co.uk',
  financial: 'https://ef-capflow.co.uk',
  ascin: 'https://ef-link.co.uk',
  projects: 'https://ef-projex.co.uk',
  portal: 'https://executiveforceai.com/portal',
  contracts: 'https://ef-contracts.com',
};

const ENV = {
  development: {
    protocol: 'http',
    host: 'localhost',
  },
  production: {
    protocol: 'https',
    host: 'europe-west2.run.app',
  },
};

const currentEnv = process.env.NODE_ENV === 'production' ? 'production' : 'development';
const { protocol, host } = ENV[currentEnv];
const isProduction = currentEnv === 'production';

/**
 * Port Configuration (Development only)
 * 
 * FRONTEND PORTS (User-facing applications):
 * - ForceAI:           3000
 * - Financial App:     3002 (CONFIRMED - running on this port)
 * - Executive AI:      3003
 * - CRM:               5174 (CONFIRMED - running on this port)
 * - ASCIN:             3004
 * - Business Planning: 5176
 * 
 * BACKEND PORTS (API services):
 * - Integration Hub:   8000
 * - Financial App:     8001
 * - Unified Admin:     8002
 * - Executive AI:      8003
 * - CRM:               8004
 * - ASCIN:             8005
 */

/**
 * Executive Force AI Application Registry
 * 
 * This is the single source of truth for all applications in the ecosystem.
 * Update this registry when adding new applications or changing ports.
 */
export const BUSINESS_APPLICATIONS: BusinessApplication[] = [
  {
    id: 'forceai',
    name: 'Executive Force AI',
    description: 'AI-Powered Business Intelligence',
    frontendUrl: isProduction ? PRODUCTION_URLS.forceai : `${protocol}://${host}:3000`,
    backendUrl: `${protocol}://${host}:8000`,
    icon: 'Psychology',
    status: 'active',
    permissions: ['read', 'write', 'admin'],
    category: 'core',
  },
  {
    id: 'financial-uk',
    name: 'EF-CapFlow',
    description: 'Complete UK financial management — EF-CapFlow',
    frontendUrl: isProduction ? PRODUCTION_URLS.financial : `${protocol}://${host}:3002`,
    backendUrl: `${protocol}://${host}:8001`,
    icon: 'AccountBalance',
    status: 'active',
    permissions: ['read', 'write', 'admin', 'finance'],
    category: 'core',
  },
  {
    id: 'crm',
    name: 'EF-CRM',
    description: 'Customer Relationship Management',
    frontendUrl: isProduction ? PRODUCTION_URLS.crm : `${protocol}://${host}:5174`,
    backendUrl: `${protocol}://${host}:8004`,
    icon: 'People',
    status: 'active',
    permissions: ['read', 'write', 'admin', 'sales', 'customer'],
    category: 'core',
  },
  {
    id: 'ascin',
    name: 'ASCIN Supply Chain',
    description: 'Adaptive Supply Chain Intelligence Network',
    frontendUrl: isProduction ? PRODUCTION_URLS.ascin : `${protocol}://${host}:3004`,
    backendUrl: `${protocol}://${host}:8005`,
    icon: 'Inventory',
    status: 'active',
    permissions: ['read', 'write', 'admin', 'supply_chain', 'logistics'],
    category: 'operations',
  },
  {
    id: 'portal',
    name: 'Portal Dashboard',
    description: 'Main Portal & Settings',
    frontendUrl: isProduction ? PRODUCTION_URLS.portal : `${protocol}://${host}:3000/portal`,
    backendUrl: '',
    icon: 'Business',
    status: 'active',
    permissions: ['read', 'write', 'admin'],
    category: 'core',
  },
  {
    id: 'hitl-monitoring',
    name: 'HITL Monitoring',
    description: 'Human-in-the-Loop Agent Oversight',
    frontendUrl: '/hitl-monitoring',
    backendUrl: `${protocol}://${host}:8000`,
    icon: 'MonitorHeart',
    status: 'active',
    permissions: ['read', 'write', 'admin', 'monitoring', 'approval'],
    category: 'monitoring',
  },
  {
    id: 'project-management',
    name: 'Project Management',
    description: 'Enterprise Project Management',
    frontendUrl: isProduction ? PRODUCTION_URLS.projects : `${protocol}://${host}:3005`,
    backendUrl: '', // Not yet built
    icon: 'Assignment',
    status: 'coming_soon',
    permissions: ['read', 'write', 'admin', 'project'],
    category: 'operations',
  },
  {
    id: 'ef-contracts',
    name: 'EF-Contracts',
    description: 'Draft, Send & E-Sign Contracts',
    frontendUrl: isProduction ? PRODUCTION_URLS.contracts : `${protocol}://${host}:5175`,
    backendUrl: isProduction ? 'https://api.ef-contracts.com' : `${protocol}://${host}:3002`,
    icon: 'Description',
    status: 'active',
    permissions: ['read', 'write', 'admin', 'contracts'],
    category: 'operations',
  },
];

/**
 * Get application by ID
 */
export const getApplicationById = (id: string): BusinessApplication | undefined => {
  return BUSINESS_APPLICATIONS.find(app => app.id === id);
};

/**
 * Get applications by category
 */
export const getApplicationsByCategory = (category: string): BusinessApplication[] => {
  return BUSINESS_APPLICATIONS.filter(app => app.category === category);
};

/**
 * Get active applications only
 */
export const getActiveApplications = (): BusinessApplication[] => {
  return BUSINESS_APPLICATIONS.filter(app => app.status === 'active');
};

/**
 * Get applications accessible by user permissions
 */
export const getAccessibleApplications = (
  userPermissions: Record<string, string[]>
): BusinessApplication[] => {
  return BUSINESS_APPLICATIONS.filter(app => {
    const appPerms = userPermissions[app.id];
    return appPerms && appPerms.length > 0;
  });
};

/**
 * Check if user has access to application
 */
export const hasAccessToApplication = (
  appId: string,
  userPermissions: Record<string, string[]>
): boolean => {
  const appPerms = userPermissions[appId];
  return appPerms && appPerms.length > 0;
};

/**
 * Get navigation URL for application.
 * Firebase SSO uses Admin-minted custom tokens; raw tokens are not passed
 * through query strings.
 */
export const getNavigationUrl = (app: BusinessApplication): string => {
  const baseUrl = app.frontendUrl;
  
  // If relative path, return as-is
  if (baseUrl.startsWith('/')) {
    return baseUrl;
  }
  
  return baseUrl;
};

/**
 * Export for use in components
 */
export default BUSINESS_APPLICATIONS;
